/**
 * Related Parties Panel — table view with stat cards, status filters,
 * and slide-out detail panel. Matches the SecurityInterests/Liabilities pattern.
 *
 * Reads canonical entries from the `relatedPartyEntries` table.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Plus } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Loader2,
  Users,
  Building2,
  User,
  Landmark,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/utils/misc";
import type { RelatedPartyEntryUI } from "./types";
import { RelatedPartyDetailPanel } from "./RelatedPartyDetailPanel";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const STATUS_BADGE: Record<
  string,
  { label: string; color: "emerald" | "amber" | "blue" | "red" }
> = {
  confirmed: { label: "Confirmed", color: "emerald" },
  probable: { label: "Probable", color: "amber" },
  possible: { label: "Possible", color: "blue" },
  excluded: { label: "Excluded", color: "red" },
};

const CATEGORY_LABELS: Record<string, string> = {
  director: "Director",
  former_director: "Former director",
  shareholder: "Shareholder",
  related_body_corporate: "Related body corporate",
  director_relative: "Director relative",
  spouse_of_director: "Spouse of director",
  controlled_entity: "Controlled entity",
  associate: "Associate",
  unknown_associated_party: "Unknown associated party",
  spouse: "Spouse (Bankruptcy Act s 5)",
  de_facto_partner: "De facto partner (s 5)",
  former_spouse: "Former spouse (s 5)",
  former_de_facto_partner: "Former de facto partner (s 5)",
  relative: "Relative (s 5)",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <div
      className={cn(
        "min-w-0 rounded-xl border border-border bg-background px-4 py-3",
        "shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]",
      )}
    >
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-0.5 text-2xl font-semibold",
          accent ?? "text-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function PartyIcon({ type, className }: { type: string; className?: string }) {
  const cls = className ?? "h-3.5 w-3.5";
  switch (type) {
    case "company":
      return <Building2 className={cls} />;
    case "trust":
      return <Landmark className={cls} />;
    default:
      return <User className={cls} />;
  }
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

export function RelatedPartiesPanel({
  jobId,
  taskId,
  isRunning,
  isViewer,
  onViewDocument: _onViewDocument,
}: {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  isViewer?: boolean;
  onViewDocument?: (docId: string) => void;
}) {
  const { data: entries, isLoading } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listRelatedPartyEntriesForJob,
      { jobId },
    ),
  );

  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"confidence" | "name" | "status">(
    "confidence",
  );

  const addParty = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.relatedParties.publicMutations
        .addManualRelatedParty,
    ),
  });

  // Lazily rewrites a bankruptcy job's corporate family categories to their
  // Bankruptcy Act s 5 equivalents (QA R-03), so existing jobs converge on
  // load without a run-once migration. No-op on corporate jobs and for viewers.
  const migrateCategories = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.relatedParties.publicMutations
        .migrateBankruptcyRelationshipCategories,
    ),
  });
  const migrate = migrateCategories.mutate;
  useEffect(() => {
    migrate({ jobId });
  }, [migrate, jobId]);

  const allParties: RelatedPartyEntryUI[] = useMemo(
    () => entries ?? [],
    [entries],
  );

  const filtered = useMemo(() => {
    const result = statusFilter
      ? allParties.filter((p) => p.relationshipStatus === statusFilter)
      : allParties.filter((p) => p.relationshipStatus !== "excluded");

    if (sortBy === "confidence")
      result.sort((a, b) => b.confidenceScore - a.confidenceScore);
    else if (sortBy === "name")
      result.sort((a, b) => a.legalName.localeCompare(b.legalName));
    else if (sortBy === "status") {
      const order: Record<string, number> = {
        confirmed: 0,
        probable: 1,
        possible: 2,
        excluded: 3,
      };
      result.sort(
        (a, b) =>
          (order[a.relationshipStatus] ?? 9) -
          (order[b.relationshipStatus] ?? 9),
      );
    }
    return result;
  }, [allParties, statusFilter, sortBy]);

  const selectedEntry = useMemo(
    () => allParties.find((p) => p._id === selectedEntryId) ?? null,
    [allParties, selectedEntryId],
  );

  // Counts
  const confirmed = allParties.filter(
    (p) => p.relationshipStatus === "confirmed",
  ).length;
  const probable = allParties.filter(
    (p) => p.relationshipStatus === "probable",
  ).length;
  const possible = allParties.filter(
    (p) => p.relationshipStatus === "possible",
  ).length;
  const excluded = allParties.filter(
    (p) => p.relationshipStatus === "excluded",
  ).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (allParties.length === 0) {
    if (isRunning) {
      return (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">
            Identifying related parties...
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="rounded-full bg-muted/50 p-4">
          <Users className="h-8 w-8 text-muted-foreground/40" />
        </div>
        <p className="text-sm text-muted-foreground">
          No related parties identified
        </p>
        {!isViewer && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() =>
              addParty.mutate(
                { jobId, taskId, legalName: "New Related Party" },
                { onSuccess: (id) => setSelectedEntryId(id as string) },
              )
            }
          >
            <Plus className="h-3.5 w-3.5" /> Add Related Party
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Count + Add + running indicator */}
      <div className="mb-3 flex items-center gap-3 text-xs text-muted-foreground">
        <span>
          {filtered.length} {filtered.length === 1 ? "party" : "parties"}
        </span>
        {!isViewer && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 gap-1 px-2 text-[11px]"
            disabled={addParty.isPending}
            onClick={() =>
              addParty.mutate(
                { jobId, taskId, legalName: "New Related Party" },
                { onSuccess: (id) => setSelectedEntryId(id as string) },
              )
            }
          >
            <Plus className="h-3 w-3" /> Add
          </Button>
        )}
        {isRunning && (
          <span className="flex items-center gap-1.5 text-blue-500">
            <Loader2 className="h-3 w-3 animate-spin" /> Updating
          </span>
        )}
      </div>

      {/* Stat cards */}
      <div className="mb-5 grid grid-cols-4 gap-3">
        <StatCard
          label="Confirmed"
          value={confirmed}
          accent="text-emerald-600"
        />
        <StatCard label="Probable" value={probable} accent="text-amber-500" />
        <StatCard label="Possible" value={possible} accent="text-blue-500" />
        <StatCard label="Excluded" value={excluded} accent="text-red-500" />
      </div>

      {/* Filter + sort row */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {(
            [
              { key: null, label: "All" },
              { key: "confirmed", label: "Confirmed" },
              { key: "probable", label: "Probable" },
              { key: "possible", label: "Possible" },
              { key: "excluded", label: "Excluded" },
            ] as const
          ).map(({ key, label }) => (
            <Button
              key={label}
              variant={statusFilter === key ? "default" : "ghost"}
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="mr-1 text-xs text-muted-foreground">Sort</span>
          {(["confidence", "name", "status"] as const).map((s) => (
            <Button
              key={s}
              variant={sortBy === s ? "outline" : "ghost"}
              size="sm"
              className="h-6 px-2 text-[11px] capitalize"
              onClick={() => setSortBy(s)}
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-10" />
              <TableHead className="text-xs font-semibold uppercase tracking-wider">
                Name
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">
                Status
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">
                Category
              </TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((party) => {
              const statusCfg =
                STATUS_BADGE[party.relationshipStatus] ?? STATUS_BADGE.possible;
              return (
                <TableRow
                  key={party._id}
                  onClick={() => setSelectedEntryId(party._id)}
                  className={cn(
                    "cursor-pointer transition-colors",
                    selectedEntryId === party._id
                      ? "bg-primary/5"
                      : "hover:bg-muted/50",
                  )}
                >
                  <TableCell className="pl-4 pr-0">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted/60">
                      <PartyIcon
                        type={party.partyType}
                        className="h-3.5 w-3.5 text-muted-foreground"
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="min-w-0">
                      <div className="max-w-[280px] truncate text-sm font-medium">
                        {party.legalName}
                      </div>
                      <div className="mt-0.5 max-w-[360px] truncate text-xs text-muted-foreground">
                        {party.summary}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="soft" color={statusCfg.color} size="sm">
                      {statusCfg.label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground">
                      {CATEGORY_LABELS[party.relationshipCategory] ??
                        party.relationshipCategory}
                    </span>
                  </TableCell>
                  <TableCell className="pr-4">
                    <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {filtered.length === 0 && (
        <div className="py-12 text-center text-sm text-muted-foreground">
          No parties match the current filter
        </div>
      )}

      {/* Detail panel */}
      <RelatedPartyDetailPanel
        entry={selectedEntry}
        open={selectedEntry !== null}
        onClose={() => setSelectedEntryId(null)}
        onDeleted={() => setSelectedEntryId(null)}
        isViewer={isViewer}
      />
    </div>
  );
}
